/**
 * Wizard Studio — creare reclame pe un singur ecran.
 * Obligatoriu: șablon (+ personaj recomandat). Brief opțional.
 * Format / voce / muzică / produs: default-uri inteligente + panou Detalii.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AUDIO_MODES, VIDEO_FORMATS, VIDEO_FORMAT_META } from '@reclame-ai/shared';
import type { AudioMode, ProductCategory, ProductTier, VideoFormat } from '@reclame-ai/shared';
import { useCharacters, useMusic, useMutation, useProducts, useTemplates, useVoices } from '../api/hooks';
import { createClip } from '../api/endpoints';
import type { CreateClipBody } from '../api/endpoints';
import type { Character, Template, TemplateMediaAsset, Voice } from '../api/types';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { Logo } from '../components/Logo';
import { Spinner } from '../components/ui';
import { VoicePlayButton } from '../components/VoicePlayButton';
import { categoryLabel, counterLabel, tierLabel } from '../lib/format';
import { resolveMediaUrl } from '../lib/media';

const MOODS = ['Energic', 'Calm', 'Urban', 'Fără muzică'] as const;

/** Cele 3 (+1) opțiuni expuse în studio — lip_sync e ascuns (viitor). */
const AUDIO_MODE_OPTIONS: { value: AudioMode; title: string; body: string }[] = [
  {
    value: 'tts',
    title: 'Voce ElevenLabs',
    body: 'Voiceover pe scenariu (TTS + timestamps). Standard, control maxim pe text.',
  },
  {
    value: 'tts_fit_video',
    title: 'EL pe ritmul video',
    body: 'TTS pe scenariu, apoi viteza se ajustează ca să se potrivească pe durata clipurilor Grok.',
  },
  {
    value: 'native',
    title: 'Sunet din video (Grok)',
    body: 'Păstrează audio-ul nativ din video, dacă modelul l-a generat.',
  },
  {
    value: 'native_voice_change',
    title: 'Grok + voice change EL',
    body: 'Audio nativ din video, convertit la o voce din catalog (timing păstrat).',
  },
];

// Sanity: only modes that exist in shared
void AUDIO_MODES;

type PreviewItem = {
  url: string;
  kind: string;
  mime?: string;
  label?: string;
};

type LightboxState = {
  items: PreviewItem[];
  index: number;
  title: string;
};

export function WizardPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const door = params.get('door');
  const preTemplate = params.get('templateId');
  const toast = useToast();

  const [category, setCategory] = useState<ProductCategory>('organic');
  const [tier, setTier] = useState<ProductTier>('premium');
  const [templateId, setTemplateId] = useState<string | null>(preTemplate);
  const [format, setFormat] = useState<VideoFormat>('9:16');
  const [characterId, setCharacterId] = useState<string | null>(null);
  const [characterTouched, setCharacterTouched] = useState(false);
  const [avatarFileId, setAvatarFileId] = useState<string | null>(null);
  const [voiceId, setVoiceId] = useState<string | null>(null);
  /** Cum se construiește vocea: TTS EL / fit pe video Grok / nativ / voice change. */
  const [audioMode, setAudioMode] = useState<AudioMode>('tts');
  const [mood, setMood] = useState<string>('Energic');
  const [musicTrackId, setMusicTrackId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [query, setQuery] = useState('');
  const [nicheFilter, setNicheFilter] = useState<string | null>(null);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  /** Index exemplu pe telefonul din slate (când șablonul are mai multe). */
  const [previewIndex, setPreviewIndex] = useState(0);
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const templates = useTemplates(category);
  const voices = useVoices();
  const music = useMusic(mood === 'Fără muzică' ? undefined : mood);
  const products = useProducts();
  const allCharacters = useCharacters();

  const charById = useMemo(() => {
    const map = new Map<string, Character>();
    for (const c of allCharacters.data?.items ?? []) map.set(c.id, c);
    return map;
  }, [allCharacters.data]);

  useEffect(() => {
    document.title = 'Studio reclame — Reclame Video';
  }, []);

  // La schimbarea categoriei (nu la mount), șablonul selectat nu mai e valid în listă.
  const categoryBoot = useRef(true);
  useEffect(() => {
    if (categoryBoot.current) {
      categoryBoot.current = false;
      return;
    }
    setTemplateId(null);
    setCharacterId(null);
    setAvatarFileId(null);
    setCharacterTouched(false);
  }, [category]);

  const template = useMemo(
    () => templates.data?.items.find((t) => t.id === templateId) ?? null,
    [templates.data, templateId],
  );

  const niches = useMemo(() => {
    const set = new Set<string>();
    for (const t of templates.data?.items ?? []) {
      if (t.niche?.trim()) set.add(t.niche.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ro'));
  }, [templates.data]);

  const filteredTemplates = useMemo(() => {
    const items = templates.data?.items ?? [];
    const q = query.trim().toLowerCase();
    return items.filter((t) => {
      if (nicheFilter && (t.niche ?? '') !== nicheFilter) return false;
      if (!q) return true;
      const hay = `${t.name} ${t.niche ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [templates.data, query, nicheFilter]);

  const templateCharacterIds = template?.characterIds ?? [];
  const charactersQuery = useCharacters(
    templateCharacterIds.length > 0 ? templateCharacterIds : undefined,
  );
  /** Fără șablon nu afișăm / preselectăm personaje (catalogul e legat de stil). */
  const characters: Character[] = template ? (charactersQuery.data?.items ?? []) : [];
  const templateAvatars: TemplateMediaAsset[] = template?.avatars ?? [];
  const selectedCharacter = characters.find((c) => c.id === characterId) ?? null;

  // Voce: recomandare AI / default șablon
  useEffect(() => {
    if (voiceId) return;
    const list = voices.data?.items ?? [];
    const rec =
      list.find((v) => v.recommended) ??
      (template?.defaultVoiceId ? list.find((v) => v.id === template.defaultVoiceId) : null) ??
      list[0];
    if (rec) setVoiceId(rec.id);
  }, [voices.data, template, voiceId]);

  // La schimbare șablon: resetează personaj + aliniază formatul
  useEffect(() => {
    setCharacterId(null);
    setAvatarFileId(null);
    setCharacterTouched(false);
    if (template) {
      const allowed = template.formats ?? [];
      if (allowed.length > 0 && !allowed.includes(format)) {
        setFormat((allowed.includes('9:16' as VideoFormat) ? '9:16' : allowed[0]!) as VideoFormat);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- doar la templateId
  }, [templateId]);

  // Preselect personaj
  useEffect(() => {
    if (characterTouched) return;
    if (characters.length > 0) {
      setCharacterId(characters[0]!.id);
      setAvatarFileId(null);
      return;
    }
    setAvatarFileId(template?.avatars?.[0]?.id ?? null);
  }, [characters, template, characterTouched]);

  // Preselect primă piesă muzicală
  useEffect(() => {
    if (mood === 'Fără muzică') {
      setMusicTrackId(null);
      return;
    }
    if (musicTrackId) return;
    const first = music.data?.items?.[0];
    if (first) setMusicTrackId(first.id);
  }, [music.data, mood, musicTrackId]);

  const createMut = useMutation({
    mutationFn: () => {
      const body: CreateClipBody = {
        templateId: templateId ?? undefined,
        category,
        tier,
        format,
        title: title.trim() || undefined,
        brief: brief.trim() || undefined,
        voiceId: voiceId ?? undefined,
        musicTrackId: mood === 'Fără muzică' ? undefined : musicTrackId ?? undefined,
        audioMode,
        productId: productId ?? undefined,
        characterId: characterId || undefined,
        avatarFileId: !characterId && avatarFileId ? avatarFileId : undefined,
      };
      return createClip(body);
    },
    onSuccess: (res) => {
      navigate(`/clipuri/${res.clip.id}`, { replace: true });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Nu am putut crea clipul', body: humanError(err) }),
  });

  const counterName = counterLabel(`${category}_${tier}`);
  const canCreate = Boolean(template) && !createMut.isPending;
  const selectedVoice = (voices.data?.items ?? []).find((v) => v.id === voiceId) ?? null;
  const selectedProduct = (products.data?.items ?? []).find((p) => p.id === productId) ?? null;

  function selectTemplate(t: Template) {
    setTemplateId(t.id);
    setPreviewIndex(0);
    if (t.formats.length > 0 && !t.formats.includes(format)) {
      setFormat((t.formats.includes('9:16' as VideoFormat) ? '9:16' : t.formats[0]!) as VideoFormat);
    }
  }

  function openExamples(t: Template, startIndex = 0) {
    const items = assetsToPreview(t.examples ?? [], 'Exemplu');
    if (items.length === 0) return;
    setLightbox({
      items,
      index: Math.min(startIndex, items.length - 1),
      title: t.name,
    });
  }

  function openCharacterGallery(ch: Character, startIndex = 0) {
    const items: PreviewItem[] = [];
    if (ch.cover?.url) {
      items.push({
        url: ch.cover.url,
        kind: ch.cover.kind || 'image',
        mime: ch.cover.mime,
        label: 'Copertă',
      });
    }
    for (const p of ch.poseAssets ?? []) {
      if (!p.url) continue;
      if (ch.cover?.url && p.url === ch.cover.url) continue;
      items.push({
        url: p.url,
        kind: p.kind || 'image',
        mime: p.mime,
        label: p.label || 'Unghi',
      });
    }
    if (items.length === 0) return;
    setLightbox({
      items,
      index: Math.min(startIndex, items.length - 1),
      title: ch.name,
    });
  }

  const templateExamples = useMemo(
    () => assetsToPreview(template?.examples ?? [], 'Exemplu'),
    [template],
  );

  const phonePreviewItems = useMemo(() => {
    // Prioritate: exemple șablon (ca să vezi stilul), apoi cover personaj
    if (templateExamples.length > 0) return templateExamples;
    if (selectedCharacter?.cover?.url) {
      return [
        {
          url: selectedCharacter.cover.url,
          kind: selectedCharacter.cover.kind || 'image',
          mime: selectedCharacter.cover.mime,
          label: selectedCharacter.name,
        },
      ];
    }
    return [] as PreviewItem[];
  }, [templateExamples, selectedCharacter]);

  const phoneItem =
    phonePreviewItems.length > 0
      ? phonePreviewItems[Math.min(previewIndex, phonePreviewItems.length - 1)]!
      : null;

  const renderCreate = () => (
    <button
      className="btn btn-primary"
      data-testid="wizard-create"
      disabled={!canCreate}
      onClick={() => createMut.mutate()}
    >
      {createMut.isPending ? <Spinner /> : 'Creează clipul →'}
    </button>
  );

  const detailsPanel = () => (
    <DetailsPanel
      format={format}
      setFormat={setFormat}
      template={template}
      productId={productId}
      setProductId={setProductId}
      products={products.data?.items ?? []}
      setBrief={setBrief}
      brief={brief}
      voiceId={voiceId}
      setVoiceId={setVoiceId}
      voices={voices.data?.items ?? []}
      audioMode={audioMode}
      setAudioMode={setAudioMode}
      mood={mood}
      setMood={(m) => {
        setMood(m);
        setMusicTrackId(null);
      }}
      musicTrackId={musicTrackId}
      setMusicTrackId={setMusicTrackId}
      musicItems={music.data?.items ?? []}
    />
  );

  return (
    <div className="wz-root">
      <header className="wz-header">
        <button type="button" className="wz-header-exit" onClick={() => navigate('/creeaza')}>
          ← Ieși din studio
        </button>
        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span className="pill" style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}>
            Nu consumi din sold până la aprobarea imaginilor
          </span>
          <Logo />
        </div>
      </header>

      <div className="wz-body">
        <main className="wz-stage rv-in">
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: 'var(--accent-text)',
            }}
          >
            Studio reclame
          </p>
          <h1
            className="bricolage"
            style={{ margin: '6px 0 0', fontWeight: 800, fontSize: 'clamp(26px, 3.2vw, 34px)' }}
          >
            Ce creăm azi?
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: 14.5, color: 'var(--muted)', maxWidth: '52ch', lineHeight: 1.55 }}>
            Alege stilul și prezentatorul, scrie pe scurt mesajul — restul e setat inteligent. Poți ajusta detaliile oricând.
          </p>

          {/* Filtre globale */}
          <div className="wz-filters" style={{ marginTop: 22 }}>
            <div className="wz-filter-row">
              <input
                className="wz-search"
                type="search"
                placeholder="Caută șablon sau nișă…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Caută șabloane"
              />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(['organic', 'reclame'] as ProductCategory[]).map((c) => (
                  <button key={c} type="button" className="chip" data-on={category === c} onClick={() => setCategory(c)}>
                    {categoryLabel(c)}
                  </button>
                ))}
                {(['premium', 'ultra'] as ProductTier[]).map((t) => (
                  <button key={t} type="button" className="chip" data-on={tier === t} onClick={() => setTier(t)}>
                    {tierLabel(t)}
                  </button>
                ))}
              </div>
            </div>
            {niches.length > 0 && (
              <div className="wz-filter-row">
                <button type="button" className="chip" data-on={!nicheFilter} onClick={() => setNicheFilter(null)}>
                  Toate nișele
                </button>
                {niches.map((n) => (
                  <button key={n} type="button" className="chip" data-on={nicheFilter === n} onClick={() => setNicheFilter(n)}>
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Produs (ușa 3) */}
          {door === 'produs' && (
            <section className="wz-section" aria-labelledby="wz-products-title">
              <div className="wz-section-head">
                <div>
                  <h2 id="wz-products-title" className="wz-section-title">
                    1. Produsul din care pornim
                  </h2>
                  <p className="wz-section-hint">AI-ul știe deja numele, prețul și pozele.</p>
                </div>
              </div>
              {(products.data?.items ?? []).length === 0 ? (
                <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--muted)' }}>
                  Nu ai încă produse. <a href="/produse">Adaugă un produs →</a>
                </div>
              ) : (
                <div className="wz-product-row" data-testid="product-list">
                  {(products.data?.items ?? []).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="card"
                      data-on={productId === p.id}
                      onClick={() => {
                        setProductId(p.id);
                        if (!brief.trim()) setBrief(p.name);
                      }}
                      style={{
                        padding: 14,
                        textAlign: 'left',
                        borderColor: productId === p.id ? 'var(--accent)' : 'var(--line)',
                      }}
                    >
                      <span style={{ fontWeight: 800, fontSize: 14 }}>{p.name}</span>
                      {p.priceText && (
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {p.priceText}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Șabloane */}
          <section className="wz-section" aria-labelledby="wz-templates-title">
            <div className="wz-section-head">
              <div>
                <h2 id="wz-templates-title" className="wz-section-title">
                  {door === 'produs' ? '2. Alege stilul' : '1. Alege stilul'}
                </h2>
                <p className="wz-section-hint">Șabloane gata de postat — click pe card ca să-l selectezi.</p>
              </div>
              <span className="wz-section-meta">
                {filteredTemplates.length} {filteredTemplates.length === 1 ? 'șablon' : 'șabloane'}
              </span>
            </div>

            {templates.isLoading ? (
              <p style={{ color: 'var(--muted)' }}>Se încarcă șabloanele…</p>
            ) : filteredTemplates.length === 0 ? (
              <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
                Niciun șablon nu se potrivește. Schimbă filtrele sau cautarea.
              </div>
            ) : (
              <div className="wz-template-grid" data-testid="template-list">
                {filteredTemplates.map((t) => {
                  const on = templateId === t.id;
                  const examples = t.examples ?? [];
                  const media = examples[0];
                  const allowedIds = t.characterIds ?? [];
                  const cardChars =
                    allowedIds.length > 0
                      ? allowedIds.map((id) => charById.get(id)).filter(Boolean).slice(0, 3)
                      : (allCharacters.data?.items ?? []).slice(0, 3);
                  return (
                    <div
                      key={t.id}
                      data-testid="template-card"
                      data-on={on}
                      className="wz-template-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => selectTemplate(t)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          selectTemplate(t);
                        }
                      }}
                    >
                      <div className="wz-template-media">
                        {on && (
                          <span className="pill wz-template-check" style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}>
                            ✓
                          </span>
                        )}
                        <MediaThumb asset={media} empty="▶" />
                        {examples.length > 0 && (
                          <button
                            type="button"
                            className="wz-template-view"
                            data-testid="template-view-examples"
                            onClick={(e) => {
                              e.stopPropagation();
                              openExamples(t, 0);
                            }}
                          >
                            Vezi {examples.length}{' '}
                            {examples.every((e) => e.kind === 'video' || e.mime?.startsWith('video/'))
                              ? examples.length === 1
                                ? 'reclamă'
                                : 'reclame'
                              : examples.length === 1
                                ? 'exemplu'
                                : 'exemple'}
                          </button>
                        )}
                      </div>
                      {examples.length > 1 && (
                        <div className="wz-template-strip" onClick={(e) => e.stopPropagation()}>
                          {examples.slice(0, 4).map((ex, i) => {
                            const isVid = ex.kind === 'video' || ex.mime?.startsWith('video/');
                            return (
                              <button
                                key={ex.id || i}
                                type="button"
                                className="wz-template-strip-item"
                                title={isVid ? `Reclamă video ${i + 1}` : `Exemplu ${i + 1}`}
                                onClick={() => openExamples(t, i)}
                              >
                                <MediaThumb asset={ex} empty="·" />
                                {isVid && <span className="wz-strip-play">▶</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="wz-template-body">
                        <span className="wz-template-name">{t.name}</span>
                        <span className="wz-template-niche">{t.niche || 'General'}</span>
                        {cardChars.length > 0 && (
                          <div className="wz-template-chars">
                            <div className="wz-char-stack">
                              {cardChars.map((ch) =>
                                ch ? (
                                  ch.cover?.url ? (
                                    <img
                                      key={ch.id}
                                      src={resolveMediaUrl(ch.cover.url) ?? ch.cover.url}
                                      alt=""
                                    />
                                  ) : (
                                    <span key={ch.id} className="ph">
                                      {ch.name.slice(0, 1)}
                                    </span>
                                  )
                                ) : null,
                              )}
                            </div>
                            {allowedIds.length === 0 && (
                              <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted)' }}>catalog</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Personaje */}
          <section className="wz-section" aria-labelledby="wz-chars-title">
            <div className="wz-section-head">
              <div>
                <h2 id="wz-chars-title" className="wz-section-title">
                  {door === 'produs' ? '3. Prezentatorul' : '2. Prezentatorul'}
                </h2>
                <p className="wz-section-hint">
                  {template
                    ? 'Personajul apare pe ecran în 2–3 unghiuri animate.'
                    : 'Selectează mai întâi un șablon — apoi vezi prezentatorii potriviți.'}
                </p>
              </div>
            </div>

            {!template ? (
              <div className="card" style={{ padding: 20, color: 'var(--muted)', fontSize: 13.5 }}>
                Alege un stil de mai sus ca să vezi personajele disponibile.
              </div>
            ) : charactersQuery.isLoading ? (
              <p style={{ color: 'var(--muted)' }}>Se încarcă personajele…</p>
            ) : characters.length > 0 ? (
              <div className="wz-char-grid" data-testid="character-list">
                {characters.map((ch) => {
                  const on = characterId === ch.id;
                  const poses = ch.poses?.length ?? ch.poseAssets?.length ?? 0;
                  const canBrowse = Boolean(ch.cover?.url || (ch.poseAssets?.length ?? 0) > 0);
                  return (
                    <div
                      key={ch.id}
                      data-testid={`character-${ch.id}`}
                      data-on={on}
                      className="wz-char-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setCharacterTouched(true);
                        setCharacterId(ch.id);
                        setAvatarFileId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCharacterTouched(true);
                          setCharacterId(ch.id);
                          setAvatarFileId(null);
                        }
                      }}
                    >
                      <div className="wz-char-cover">
                        {ch.cover?.url ? (
                          <img src={resolveMediaUrl(ch.cover.url) ?? ch.cover.url} alt="" />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: 'var(--bg2)' }} />
                        )}
                        {canBrowse && (
                          <button
                            type="button"
                            className="wz-char-view"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCharacterGallery(ch, 0);
                            }}
                          >
                            Vezi unghiuri
                          </button>
                        )}
                      </div>
                      {(ch.poseAssets?.length ?? 0) > 1 && (
                        <div className="wz-char-strip" onClick={(e) => e.stopPropagation()}>
                          {(ch.poseAssets ?? []).slice(0, 4).map((p, i) => (
                            <button
                              key={p.id || i}
                              type="button"
                              className="wz-template-strip-item"
                              title={p.label || `Unghi ${i + 1}`}
                              onClick={() => openCharacterGallery(ch, ch.cover?.url ? i + 1 : i)}
                            >
                              <img src={resolveMediaUrl(p.url) ?? p.url} alt="" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="wz-char-info">
                        <span className="wz-char-name">{ch.name}</span>
                        <span className="wz-char-meta">
                          {poses} unghiuri{ch.style ? ` · ${ch.style}` : ''}
                        </span>
                        {on && (
                          <span className="pill" style={{ alignSelf: 'flex-start', marginTop: 4, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
                            ✓ Ales
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  data-on={characterId === null && !avatarFileId}
                  className="wz-char-card"
                  onClick={() => {
                    setCharacterTouched(true);
                    setCharacterId(null);
                    setAvatarFileId(null);
                  }}
                >
                  <div className="wz-char-none">
                    <span style={{ fontSize: 28, color: 'var(--muted)' }}>∅</span>
                    <span className="wz-char-name">Fără personaj</span>
                    <span className="wz-char-meta" style={{ textAlign: 'center' }}>
                      doar voce + cadre AI
                    </span>
                  </div>
                </button>
              </div>
            ) : templateAvatars.length > 0 ? (
              <div className="wz-char-grid">
                {templateAvatars.map((av, i) => {
                  const on = avatarFileId === av.id;
                  return (
                    <button
                      key={av.id}
                      type="button"
                      data-on={on}
                      className="wz-char-card"
                      onClick={() => {
                        setCharacterTouched(true);
                        setAvatarFileId(av.id);
                        setCharacterId(null);
                      }}
                    >
                      <div className="wz-char-cover">
                        <MediaThumb asset={av} />
                      </div>
                      <div className="wz-char-info">
                        <span className="wz-char-name">
                          {av.originalName?.replace(/\.[^.]+$/, '') || `Profil ${i + 1}`}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="card" style={{ padding: 18 }}>
                <span style={{ fontWeight: 800 }}>Niciun personaj în catalog</span>
                <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
                  Poți continua fără — AI-ul generează cadrele. Adminul poate adăuga personaje din panoul Personaje.
                </p>
              </div>
            )}
          </section>

          {/* Brief */}
          <section className="wz-section" aria-labelledby="wz-brief-title">
            <div className="wz-section-head">
              <div>
                <h2 id="wz-brief-title" className="wz-section-title">
                  {door === 'produs' ? '4. Mesajul tău' : '3. Mesajul tău'}
                </h2>
                <p className="wz-section-hint">Câteva propoziții sunt suficiente — AI-ul scrie scenariul complet.</p>
              </div>
            </div>
            <div className="wz-brief-block">
              <label className="label">
                Titlu (opțional)
                <input
                  className="input"
                  placeholder="Ex. Ofertă prânz — vara"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label className="label">
                Ce vrei să transmită reclama?
                <textarea
                  className="input"
                  rows={4}
                  placeholder="Ex. Prezentăm meniul de prânz la 29 lei, ton prietenos, call-to-action rezervă acum"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  data-testid="wizard-brief"
                />
              </label>
            </div>
          </section>

          {/* Detalii pe mobil (în stream; ascuns pe desktop) */}
          <section className="wz-section wz-mobile-only" aria-label="Detalii">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setMobileDetailsOpen((o) => !o)}
                className="wz-mobile-details-toggle"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontWeight: 800,
                  fontSize: 14,
                }}
              >
                <span>Detalii (format, voce, muzică{door !== 'produs' ? ', produs' : ''})</span>
                <span style={{ color: 'var(--muted)' }}>{mobileDetailsOpen ? '▴' : '▾'}</span>
              </button>
              {mobileDetailsOpen && <div style={{ padding: '0 16px 16px' }}>{detailsPanel()}</div>}
            </div>
          </section>
        </main>

        {/* Slate desktop: scroll pe conținut + CTA fix jos */}
        <aside className="wz-slate" aria-label="Recapitulare clip">
          <div className="wz-slate-scroll">
            <div className="wz-phone">
              <div
                className="wz-phone-screen"
                role={phoneItem ? 'button' : undefined}
                tabIndex={phoneItem ? 0 : undefined}
                onClick={() => {
                  if (!phonePreviewItems.length) return;
                  setLightbox({
                    items: phonePreviewItems,
                    index: Math.min(previewIndex, phonePreviewItems.length - 1),
                    title: template?.name ?? selectedCharacter?.name ?? 'Preview',
                  });
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && phonePreviewItems.length) {
                    e.preventDefault();
                    setLightbox({
                      items: phonePreviewItems,
                      index: Math.min(previewIndex, phonePreviewItems.length - 1),
                      title: template?.name ?? selectedCharacter?.name ?? 'Preview',
                    });
                  }
                }}
                title={phoneItem ? 'Click pentru a mări' : undefined}
              >
                {phoneItem ? (
                  <MediaThumb asset={phoneItem} fill />
                ) : (
                  <div className="wz-phone-empty">Alege un șablon ca să vezi exemplele</div>
                )}
                {(template || selectedCharacter) && (
                  <div className="wz-phone-overlay">
                    <strong>{template?.name ?? 'Clip nou'}</strong>
                    <span>
                      {selectedCharacter?.name ?? 'Fără personaj'} · {format}
                      {phonePreviewItems.length > 1
                        ? ` · ${Math.min(previewIndex, phonePreviewItems.length - 1) + 1}/${phonePreviewItems.length}`
                        : ''}
                    </span>
                  </div>
                )}
              </div>
              {phonePreviewItems.length > 1 && (
                <div className="wz-phone-nav" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="wz-phone-nav-btn"
                    aria-label="Exemplu anterior"
                    onClick={() =>
                      setPreviewIndex((i) => (i - 1 + phonePreviewItems.length) % phonePreviewItems.length)
                    }
                  >
                    ‹
                  </button>
                  <div className="wz-phone-dots">
                    {phonePreviewItems.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className="wz-phone-dot"
                        data-on={i === previewIndex}
                        aria-label={`Exemplu ${i + 1}`}
                        onClick={() => setPreviewIndex(i)}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="wz-phone-nav-btn"
                    aria-label="Exemplu următor"
                    onClick={() => setPreviewIndex((i) => (i + 1) % phonePreviewItems.length)}
                  >
                    ›
                  </button>
                </div>
              )}
              {phonePreviewItems.length > 0 && (
                <button
                  type="button"
                  className="wz-phone-expand"
                  onClick={() =>
                    setLightbox({
                      items: phonePreviewItems,
                      index: Math.min(previewIndex, phonePreviewItems.length - 1),
                      title: template?.name ?? 'Exemple',
                    })
                  }
                >
                  Mărește exemplele ↗
                </button>
              )}
            </div>

            <div className="wz-recap">
              <RecapRow k="Șablon" v={template?.name ?? '— alege'} />
              <RecapRow k="Personaj" v={selectedCharacter?.name ?? (avatarFileId ? 'Profil' : '—')} />
              <RecapRow k="Format" v={format} />
              <RecapRow k="Mod audio" v={audioModeLabel(audioMode)} />
              <RecapRow
                k="Voce"
                v={
                  audioMode === 'native'
                    ? 'din video'
                    : selectedVoice?.name ?? 'recomandată'
                }
              />
              <RecapRow k="Muzică" v={mood} />
              {selectedProduct && <RecapRow k="Produs" v={selectedProduct.name} />}
              <RecapRow k="Pachet" v={`${categoryLabel(category)} · ${tierLabel(tier)}`} />
            </div>

            <details className="wz-details" open>
              <summary>
                <span>Detalii</span>
                <span className="wz-details-hint">format · voce · muzică · produs</span>
              </summary>
              <div className="wz-details-body">{detailsPanel()}</div>
            </details>
          </div>

          <div className="wz-slate-cta">
            <div
              className="pill"
              style={{ alignSelf: 'stretch', justifyContent: 'center', background: 'var(--warn-soft)', color: 'var(--warn)' }}
              data-testid="cost-notice"
            >
              Cost: 1 clip „{counterName}" — după aprobarea imaginilor
            </div>
            {renderCreate()}
            {!templateId && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
                Selectează un șablon ca să poți crea.
              </p>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile sticky bar */}
      <div className="wz-mobile-bar">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {template?.name ?? 'Alege un șablon'}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>1× {counterName}</div>
        </div>
        {renderCreate()}
      </div>

      {lightbox && (
        <ExampleLightbox
          title={lightbox.title}
          items={lightbox.items}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : null))}
        />
      )}
    </div>
  );
}

function assetsToPreview(
  assets: TemplateMediaAsset[] | { url: string; kind?: string; mime?: string; label?: string }[],
  defaultLabel: string,
): PreviewItem[] {
  return (assets ?? [])
    .filter((a) => Boolean(a?.url))
    .map((a, i) => {
      const kind = ('kind' in a && a.kind) || 'image';
      const mime = 'mime' in a ? a.mime : undefined;
      const isVid = kind === 'video' || (typeof mime === 'string' && mime.startsWith('video/'));
      return {
        url: a.url,
        kind,
        mime,
        label:
          ('label' in a && a.label) ||
          (isVid ? `Reclamă video ${i + 1}` : `${defaultLabel} ${i + 1}`),
      };
    });
}

function ExampleLightbox({
  title,
  items,
  index,
  onClose,
  onIndex,
}: {
  title: string;
  items: PreviewItem[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const safeIndex = Math.min(Math.max(0, index), Math.max(0, items.length - 1));
  const current = items[safeIndex];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onIndex((safeIndex - 1 + items.length) % items.length);
      if (e.key === 'ArrowRight') onIndex((safeIndex + 1) % items.length);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, onIndex, safeIndex, items.length]);

  if (!current) return null;
  const src = resolveMediaUrl(current.url) ?? current.url;
  const isVideo = current.kind === 'video' || (current.mime?.startsWith('video/') ?? false);

  return (
    <div
      className="wz-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Exemple: ${title}`}
      data-testid="example-lightbox"
      onClick={onClose}
    >
      <div className="wz-lightbox-panel" onClick={(e) => e.stopPropagation()}>
        <header className="wz-lightbox-head">
          <div>
            <p className="wz-lightbox-kicker">Exemplu</p>
            <h2 className="bricolage wz-lightbox-title">{title}</h2>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Închide">
            ✕
          </button>
        </header>

        <div className="wz-lightbox-stage">
          {items.length > 1 && (
            <button
              type="button"
              className="wz-lightbox-nav wz-lightbox-nav-prev"
              aria-label="Anterior"
              onClick={() => onIndex((safeIndex - 1 + items.length) % items.length)}
            >
              ‹
            </button>
          )}
          <div className="wz-lightbox-frame">
            {isVideo ? (
              <video src={src} controls playsInline autoPlay muted loop />
            ) : (
              <img src={src} alt={current.label || title} />
            )}
          </div>
          {items.length > 1 && (
            <button
              type="button"
              className="wz-lightbox-nav wz-lightbox-nav-next"
              aria-label="Următor"
              onClick={() => onIndex((safeIndex + 1) % items.length)}
            >
              ›
            </button>
          )}
        </div>

        <footer className="wz-lightbox-foot">
          <span className="wz-lightbox-meta">
            {current.label || `Exemplu ${safeIndex + 1}`}
            {items.length > 1 ? ` · ${safeIndex + 1} / ${items.length}` : ''}
          </span>
          {items.length > 1 && (
            <div className="wz-lightbox-thumbs">
              {items.map((it, i) => {
                const thumb = resolveMediaUrl(it.url) ?? it.url;
                const v = it.kind === 'video' || (it.mime?.startsWith('video/') ?? false);
                return (
                  <button
                    key={`${it.url}-${i}`}
                    type="button"
                    className="wz-lightbox-thumb"
                    data-on={i === safeIndex}
                    onClick={() => onIndex(i)}
                    aria-label={it.label || `Exemplu ${i + 1}`}
                  >
                    {v ? <span className="wz-lightbox-thumb-play">▶</span> : <img src={thumb} alt="" />}
                  </button>
                );
              })}
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}

function DetailsPanel({
  format,
  setFormat,
  template,
  productId,
  setProductId,
  products,
  setBrief,
  brief,
  voiceId,
  setVoiceId,
  voices,
  audioMode,
  setAudioMode,
  mood,
  setMood,
  musicTrackId,
  setMusicTrackId,
  musicItems,
}: {
  format: VideoFormat;
  setFormat: (f: VideoFormat) => void;
  template: Template | null;
  productId: string | null;
  setProductId: (id: string | null) => void;
  products: { id: string; name: string; priceText?: string | null }[];
  setBrief: (b: string) => void;
  brief: string;
  voiceId: string | null;
  setVoiceId: (id: string) => void;
  voices: Voice[];
  audioMode: AudioMode;
  setAudioMode: (m: AudioMode) => void;
  mood: string;
  setMood: (m: string) => void;
  musicTrackId: string | null;
  setMusicTrackId: (id: string | null) => void;
  musicItems: { id: string; title: string; mood: string | null; url?: string | null }[];
}) {
  const needsVoicePicker =
    audioMode === 'tts' || audioMode === 'tts_fit_video' || audioMode === 'native_voice_change' || audioMode === 'lip_sync';

  return (
    <>
      <div>
        <div className="wz-details-label">Mod audio</div>
        <div className="wz-audio-modes" role="radiogroup" aria-label="Mod audio">
          {AUDIO_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={audioMode === opt.value}
              className="wz-audio-mode"
              data-on={audioMode === opt.value}
              onClick={() => setAudioMode(opt.value)}
            >
              <strong>{opt.title}</strong>
              <span>{opt.body}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="wz-details-label">Format</div>
        <div className="wz-format-row">
          {VIDEO_FORMATS.map((f) => {
            const disabled = template ? template.formats.length > 0 && !template.formats.includes(f) : false;
            return (
              <button
                key={f}
                type="button"
                className="wz-format-btn"
                data-testid={`format-${f}`}
                data-on={format === f}
                disabled={disabled}
                onClick={() => setFormat(f)}
                title={VIDEO_FORMAT_META[f]?.label}
              >
                {f}
              </button>
            );
          })}
        </div>
      </div>

      {products.length > 0 && (
        <div>
          <div className="wz-details-label">Produs (opțional)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button type="button" className="chip" data-on={!productId} onClick={() => setProductId(null)}>
              Fără
            </button>
            {products.map((p) => (
              <button
                key={p.id}
                type="button"
                className="chip"
                data-on={productId === p.id}
                onClick={() => {
                  setProductId(p.id);
                  if (!brief.trim()) setBrief(p.name);
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {needsVoicePicker ? (
        <div>
          <div className="wz-details-label">
            {audioMode === 'native_voice_change' ? 'Voce țintă (voice change)' : 'Voce ElevenLabs'}
          </div>
          <div className="wz-voice-list">
            {voices.map((v) => (
              <div key={v.id} className="wz-pick" data-on={voiceId === v.id} style={{ cursor: 'default' }}>
                <button
                  type="button"
                  onClick={() => setVoiceId(v.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, textAlign: 'left', minWidth: 0 }}
                >
                  <span style={{ opacity: 0.7 }}>{v.gender === 'masculin' ? '♂' : '♀'}</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: 12.5 }}>{v.name}</span>
                  {(v.recommended || v.isDefaultRecommendable) && (
                    <span className="pill" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)', fontSize: 10 }}>
                      AI
                    </span>
                  )}
                </button>
                <VoicePlayButton voiceId={v.id} sampleUrl={v.sampleUrl} label={v.name} />
              </div>
            ))}
            {voices.length === 0 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Se încarcă…</span>}
          </div>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>
          Folosești sunetul generat odată cu video-ul (Grok). Nu se alege o voce din catalog.
        </p>
      )}

      <div>
        <div className="wz-details-label">Muzică</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {MOODS.map((m) => (
            <button key={m} type="button" className="chip" data-on={mood === m} onClick={() => setMood(m)}>
              {m}
            </button>
          ))}
        </div>
        {mood !== 'Fără muzică' && (
          <div className="wz-music-list">
            {musicItems.map((tr) => (
              <div key={tr.id} className="wz-pick" data-on={musicTrackId === tr.id} style={{ cursor: 'default' }}>
                <button
                  type="button"
                  onClick={() => setMusicTrackId(tr.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, textAlign: 'left', minWidth: 0 }}
                >
                  <span style={{ flex: 1, fontWeight: 700, fontSize: 12.5 }}>{tr.title}</span>
                  {tr.mood && <span style={{ color: 'var(--muted)', fontSize: 11 }}>{tr.mood}</span>}
                </button>
                {tr.url && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    aria-label={`Ascultă ${tr.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      playAudio(tr.url!);
                    }}
                    style={{ padding: '4px 8px', fontSize: 11 }}
                  >
                    ▶
                  </button>
                )}
              </div>
            ))}
            {musicItems.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Nicio piesă — poți continua.</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

let previewAudio: HTMLAudioElement | null = null;
function playAudio(url: string) {
  try {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio = null;
    }
    const a = new Audio(url);
    previewAudio = a;
    void a.play();
  } catch {
    /* ignore autoplay blocks */
  }
}

function audioModeLabel(mode: AudioMode): string {
  return AUDIO_MODE_OPTIONS.find((o) => o.value === mode)?.title ?? mode;
}

function RecapRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="wz-recap-row">
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function MediaThumb({
  asset,
  empty = '·',
  fill,
}: {
  asset?: TemplateMediaAsset | { url: string; kind: string; mime?: string } | null;
  empty?: string;
  fill?: boolean;
}) {
  if (!asset?.url) {
    return <div className="wz-template-media-empty">{empty}</div>;
  }
  const src = resolveMediaUrl(asset.url) ?? asset.url;
  const isVideo =
    ('kind' in asset && asset.kind === 'video') ||
    ('mime' in asset && typeof asset.mime === 'string' && asset.mime.startsWith('video/'));
  const style = fill ? { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' as const } : undefined;
  if (isVideo) {
    return (
      <video
        src={src}
        muted
        playsInline
        loop
        autoPlay
        preload="metadata"
        style={style ?? { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return <img src={src} alt="" style={style ?? { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
}
